/**
 * Times in AI-written text about a recording ("00:12:30", "[4:05]"): turned
 * into links that play the recording from there, and read back as chapters
 * for "Copy YouTube chapters". Pure (shared by the page, the chat and tests).
 */

export const TIMESTAMP_HREF_PREFIX = '#t=';

/** "1:02:03" → 3723, "4:05" → 245, "[00:12]" → 12. Null when it isn't a time. */
export function parseTimestamp(value: string): number | null {
  // Minutes may run past 59 without hours ("95:00").
  const match =
    /^\[?\s*(?:(\d{1,2}):)?(\d{1,3}):(\d{2})(?:[.,]\d+)?\s*\]?$/.exec(
      value.trim(),
    );
  if (!match) return null;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (seconds > 59 || (match[1] !== undefined && minutes > 59)) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

/** 0 → "0:00", 245 → "4:05", 3723 → "1:02:03" (YouTube's style). */
export function formatTimestamp(totalSeconds: number) {
  const whole = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  const ss = String(seconds).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
    : `${minutes}:${ss}`;
}

export const timestampHref = (seconds: number) =>
  `${TIMESTAMP_HREF_PREFIX}${Math.max(0, Math.floor(seconds))}`;

/** The seconds in a link made by linkifyTimestamps, else null. */
export function secondsFromTimestampHref(href: string | null | undefined) {
  if (!href || !href.startsWith(TIMESTAMP_HREF_PREFIX)) return null;
  const seconds = Number(href.slice(TIMESTAMP_HREF_PREFIX.length));
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

// A time, optionally in brackets, not part of a longer number or a clock
// time ("8:00 AM"), and not already a link's text.
const TIME_IN_TEXT =
  /(^|[^\w:.[])(\[?)((?:\d{1,2}:)?\d{1,3}:\d{2})(\]?)(?![\w:]|\s?(?:[AaPp]\.?[Mm]\b\.?|[Uu]hr\b|h\b)|\]?\()/g;

// "[00:12 – 01:30]": the brackets go, the two times become links.
const BRACKETED_RANGE =
  /\[\s*((?:\d{1,2}:)?\d{1,3}:\d{2})\s*(-|–|—|~|to)\s*((?:\d{1,2}:)?\d{1,3}:\d{2})\s*\](?!\()/g;

/**
 * Markdown with every recording time turned into a `#t=<seconds>` link. Code
 * is left alone, and so are times past the end of the recording (when its
 * length is known), which are more likely clock times or scores.
 */
export function linkifyTimestamps(
  markdown: string,
  options: { maxSeconds?: number | null } = {},
): string {
  if (!markdown) return markdown;
  const limit =
    options.maxSeconds && options.maxSeconds > 0
      ? options.maxSeconds + 5
      : Infinity;
  // Fenced code blocks and inline code stay as written.
  return markdown
    .split(/(```[\s\S]*?```|`[^`\n]*`)/g)
    .map((part, index) => {
      if (index % 2 === 1) return part;
      return part
        .replace(BRACKETED_RANGE, '$1 $2 $3')
        .replace(
          TIME_IN_TEXT,
          (
            whole,
            before: string,
            open: string,
            time: string,
            close: string,
          ) => {
            const seconds = parseTimestamp(time);
            if (seconds === null || seconds > limit) return whole;
            const link = `[${time}](${timestampHref(seconds)})`;
            // "[00:12]" becomes the link; a bracket around a range stays as text.
            if (open && close) return `${before}${link}`;
            return `${before}${open ? '\\[' : ''}${link}${close ? '\\]' : ''}`;
          },
        );
    })
    .join('');
}

export type TranscriptChapter = { seconds: number; title: string };

const MAX_TITLE_CHARS = 80;

// Markdown and decoration around a title: bold, emphasis, links, emoji.
const cleanTitle = (raw: string) =>
  raw
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~]+/g, '')
    .replace(/\p{Extended_Pictographic}️?/gu, '')
    .replace(/^[\s\-–—|:•·.,)]+/, '')
    .replace(/\s+/g, ' ')
    .trim();

// Words whose full stop doesn't end a sentence.
const ABBREVIATIONS = new Set([
  'vs',
  'e.g',
  'i.e',
  'etc',
  'dr',
  'mr',
  'mrs',
  'ms',
  'st',
  'no',
  'approx',
]);

/** The title up to its first sentence end, dash or colon. */
const firstPart = (title: string) => {
  // "Background & Injuries- 26-year-old…": a dash glued to the word.
  const unglued = title.replace(/(\p{L})-\s.*$/u, '$1');
  const separator = /\s[-–—|]\s|[.!?。]\s|:\s/g;
  for (const match of Array.from(unglued.matchAll(separator))) {
    const end = match.index ?? 0;
    if (match[0].startsWith('.')) {
      const word = /([\p{L}.]+)$/u.exec(unglued.slice(0, end))?.[1] || '';
      if (ABBREVIATIONS.has(word.toLowerCase())) continue;
    }
    return unglued.slice(0, end);
  }
  return unglued;
};

/** A short title: up to the first sentence break or dash, at most 80 chars. */
const shortTitle = (raw: string) => {
  const title = cleanTitle(raw);
  let short = firstPart(title).trim() || title;
  // A quote opened but cut before it closes.
  if (/^["“]/.test(short) && !/["”]/.test(short.slice(1))) {
    short = short.slice(1);
  }
  if (short.length <= MAX_TITLE_CHARS) return short;
  const clipped = short.slice(0, MAX_TITLE_CHARS);
  const space = clipped.lastIndexOf(' ');
  return `${(space > 40 ? clipped.slice(0, space) : clipped).trim()}…`;
};

const TIME = String.raw`\[?\s*((?:\d{1,2}:)?\d{1,3}:\d{2})\s*\]?`;
const RANGE_END = String.raw`(?:\s*[-–—~to]+\s*\[?\s*(?:\d{1,2}:)?\d{1,3}:\d{2}\s*\]?)?`;
// "## 00:00 Intro", "## [00:00:03 – 00:00:30] Background", "### 🕐 00:02–01:12 - Case"
const HEADING_LINE = new RegExp(
  String.raw`^#{1,6}\s+(?:[^\w\s[]+\s*)?${TIME}${RANGE_END}\s*[-–—|:]?\s*(.+)$`,
  'u',
);
// "**[00:00 – 00:10]** - **Status**", "- **00:00** Intro"
const LEAD_LINE = new RegExp(
  String.raw`^(?:[-*+]\s+)?\*{0,2}${TIME}${RANGE_END}\*{0,2}\s*[-–—|:]?\s*(.+)$`,
  'u',
);
// "| **00:00:00 – 00:00:06** | Introduction | …"
const TABLE_ROW = new RegExp(
  String.raw`^\|\s*\*{0,2}${TIME}${RANGE_END}\*{0,2}\s*\|\s*([^|]+)\|`,
  'u',
);

/**
 * Chapters in a Timestamped Overview: lines that start with a time, read as
 * that time and a short title. In time order, one per time.
 */
export function parseChapters(markdown: string | null | undefined) {
  const chapters: TranscriptChapter[] = [];
  for (const rawLine of (markdown || '').split('\n')) {
    const line = rawLine.trim();
    const match =
      HEADING_LINE.exec(line) || TABLE_ROW.exec(line) || LEAD_LINE.exec(line);
    if (!match) continue;
    const seconds = parseTimestamp(match[1]);
    const title = shortTitle(match[2] || '');
    if (seconds === null || !title) continue;
    chapters.push({ seconds, title });
  }
  const sorted = chapters.sort((a, b) => a.seconds - b.seconds);
  return sorted.filter(
    (chapter, index) =>
      index === 0 || chapter.seconds !== sorted[index - 1].seconds,
  );
}

export const YOUTUBE_MIN_CHAPTERS = 3;
export const YOUTUBE_MIN_CHAPTER_SECONDS = 10;

/**
 * Chapters as YouTube wants them in a description: the first at 0:00, at
 * least 3, each at least 10 seconds long, in time order. Null when the
 * overview doesn't have enough of them.
 */
export function youtubeChapterText(
  chapters: readonly TranscriptChapter[],
  durationSeconds?: number | null,
): string | null {
  const kept: TranscriptChapter[] = [];
  for (const chapter of chapters) {
    if (durationSeconds && chapter.seconds >= durationSeconds) break;
    const previous = kept[kept.length - 1];
    if (!previous) {
      kept.push({ seconds: 0, title: chapter.title });
      continue;
    }
    if (chapter.seconds - previous.seconds < YOUTUBE_MIN_CHAPTER_SECONDS) {
      continue;
    }
    kept.push(chapter);
  }
  const last = kept[kept.length - 1];
  if (
    last &&
    durationSeconds &&
    durationSeconds - last.seconds < YOUTUBE_MIN_CHAPTER_SECONDS &&
    kept.length > 1
  ) {
    kept.pop();
  }
  if (kept.length < YOUTUBE_MIN_CHAPTERS) return null;
  const longHours = kept.some((chapter) => chapter.seconds >= 3600);
  return kept
    .map((chapter) => {
      const time = formatTimestamp(chapter.seconds);
      // With an hour or more, every time gets the hours ("0:12:30").
      const padded =
        longHours && chapter.seconds < 3600
          ? `0:${time.padStart(5, '0')}`
          : time;
      return `${padded} ${chapter.title}`;
    })
    .join('\n');
}
